// Reading a CAD BOM export in the browser: CSV/TSV/TXT, or Excel .xlsx (unzipped with the browser's own
// DecompressionStream, so no library). Then find the header row, guess which column is which, and turn rows
// into levels: from a Level column, or from the item/find number (1, 1.1, 1.1.2 …), else everything is level 1.

export type Table = string[][];

export async function readTable(file: File): Promise<Table> {
  const name = file.name.toLowerCase();
  if (name.endsWith(".xls")) throw new Error("That's an older .xls file. Save it as .xlsx or .csv and import that.");
  if (name.endsWith(".xlsx") || name.endsWith(".xlsm")) return readXlsx(new Uint8Array(await file.arrayBuffer()));
  const bytes = new Uint8Array(await file.arrayBuffer());
  let text = new TextDecoder("utf-8").decode(bytes);
  if (text.includes("�")) text = new TextDecoder("windows-1252").decode(bytes); // SOLIDWORKS CSVs are often ANSI
  return parseDelimited(text.replace(/^﻿/, ""));
}

export function parseDelimited(text: string): Table {
  const sample = text.split(/\r?\n/).slice(0, 10).join("\n");
  const count = (ch: string) => sample.split(ch).length;
  const sep = count("\t") > count(",") && count("\t") > count(";") ? "\t" : count(";") > count(",") ? ";" : ",";
  const rows: Table = [];
  let row: string[] = [], cell = "", q = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (q) {
      if (ch === '"') { if (text[i + 1] === '"') { cell += '"'; i++; } else q = false; }
      else cell += ch;
    } else if (ch === '"' && cell === "") q = true;
    else if (ch === sep) { row.push(cell); cell = ""; }
    else if (ch === "\n" || ch === "\r") { if (ch === "\r" && text[i + 1] === "\n") i++; row.push(cell); rows.push(row); row = []; cell = ""; }
    else cell += ch;
  }
  if (cell || row.length) { row.push(cell); rows.push(row); }
  return rows.map((r) => r.map((c) => c.trim())).filter((r) => r.some((c) => c));
}

// ----- .xlsx: a zip of XML parts -----
async function inflate(data: Uint8Array): Promise<Uint8Array> {
  const ds = new DecompressionStream("deflate-raw");
  const out = new Blob([data as BlobPart]).stream().pipeThrough(ds);
  return new Uint8Array(await new Response(out).arrayBuffer());
}
async function unzip(buf: Uint8Array, want: (name: string) => boolean): Promise<Map<string, string>> {
  const dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  let eocd = -1;
  for (let i = buf.length - 22; i >= Math.max(0, buf.length - 65557); i--) if (dv.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
  if (eocd < 0) throw new Error("That file isn't a readable .xlsx workbook.");
  const entries = dv.getUint16(eocd + 10, true);
  let p = dv.getUint32(eocd + 16, true);
  const out = new Map<string, string>();
  const dec = new TextDecoder();
  for (let i = 0; i < entries; i++) {
    if (dv.getUint32(p, true) !== 0x02014b50) break;
    const method = dv.getUint16(p + 10, true), size = dv.getUint32(p + 20, true);
    const nameLen = dv.getUint16(p + 28, true), extraLen = dv.getUint16(p + 30, true), commentLen = dv.getUint16(p + 32, true);
    const local = dv.getUint32(p + 42, true);
    const name = dec.decode(buf.subarray(p + 46, p + 46 + nameLen));
    p += 46 + nameLen + extraLen + commentLen;
    if (!want(name)) continue;
    const start = local + 30 + dv.getUint16(local + 26, true) + dv.getUint16(local + 28, true);
    const raw = buf.subarray(start, start + size);
    out.set(name, dec.decode(method === 0 ? raw : await inflate(raw)));
  }
  return out;
}
const colIndex = (ref: string) => { let n = 0; for (const ch of ref.replace(/\d+/g, "")) n = n * 26 + (ch.charCodeAt(0) - 64); return n - 1; };

export async function readXlsx(buf: Uint8Array): Promise<Table> {
  const files = await unzip(buf, (n) => n === "xl/sharedStrings.xml" || n === "xl/workbook.xml" || n === "xl/_rels/workbook.xml.rels" || /^xl\/worksheets\/sheet\d+\.xml$/.test(n));
  const xml = (s: string) => new DOMParser().parseFromString(s, "application/xml");
  const strings: string[] = [];
  const ss = files.get("xl/sharedStrings.xml");
  if (ss) for (const si of Array.from(xml(ss).getElementsByTagName("si"))) strings.push(Array.from(si.getElementsByTagName("t")).map((t) => t.textContent ?? "").join(""));
  // The first sheet in the workbook's order.
  let sheetPath = [...files.keys()].filter((k) => k.startsWith("xl/worksheets/")).sort()[0];
  const wb = files.get("xl/workbook.xml"), rels = files.get("xl/_rels/workbook.xml.rels");
  if (wb && rels) {
    const first = xml(wb).getElementsByTagName("sheet")[0];
    const rid = first?.getAttribute("r:id") ?? first?.getAttributeNS("http://schemas.openxmlformats.org/officeDocument/2006/relationships", "id");
    const rel = Array.from(xml(rels).getElementsByTagName("Relationship")).find((r) => r.getAttribute("Id") === rid);
    const target = rel?.getAttribute("Target");
    if (target) { const t = target.replace(/^\/?(xl\/)?/, "xl/"); if (files.has(t)) sheetPath = t; }
  }
  if (!sheetPath) throw new Error("That workbook has no sheets.");
  const doc = xml(files.get(sheetPath)!);
  const table: Table = [];
  for (const r of Array.from(doc.getElementsByTagName("row"))) {
    const row: string[] = [];
    for (const c of Array.from(r.getElementsByTagName("c"))) {
      const t = c.getAttribute("t"), ref = c.getAttribute("r") ?? "";
      const v = c.getElementsByTagName("v")[0]?.textContent ?? "";
      const val = t === "s" ? strings[Number(v)] ?? "" : t === "inlineStr" ? Array.from(c.getElementsByTagName("t")).map((x) => x.textContent ?? "").join("") : t === "b" ? (v === "1" ? "TRUE" : "FALSE") : v;
      row[ref ? colIndex(ref) : row.length] = val.trim();
    }
    table.push(Array.from(row, (x) => x ?? ""));
  }
  return table.filter((r) => r.some((c) => c));
}

// ----- Columns -----
export type Role = "partNumber" | "description" | "quantity" | "position" | "level" | "revision" | "material" | "uom" | "structure";
export const ROLES: { id: Role; name: string; required?: boolean }[] = [
  { id: "partNumber", name: "Part Number", required: true }, { id: "quantity", name: "Quantity", required: true }, { id: "description", name: "Description" },
  { id: "position", name: "Item / Find No." }, { id: "level", name: "Level" }, { id: "revision", name: "Revision" }, { id: "material", name: "Material" },
  { id: "uom", name: "Unit of Measure" }, { id: "structure", name: "Make / Buy" },
];
const PATTERNS: Record<Role, RegExp> = {
  partNumber: /^(part\s*(no|number|num|#)\.?|partno|pn|p\/n|number|item\s*number|component)$/i,
  description: /^(description|desc\.?|name|title|part\s*name)$/i,
  quantity: /^(qty\.?|quantity|qty\s*per|unit\s*qty|total\s*qty\.?)$/i,
  position: /^(item(\s*no\.?)?|find\s*(no\.?|number)?|pos(ition)?\.?|balloon|#)$/i,
  level: /^(level|lvl|bom\s*level|indent)$/i,
  revision: /^(rev(ision)?\.?)$/i,
  material: /^(material|mat'?l)$/i,
  uom: /^(unit|uom|u\/m|unit of measure|base unit( of measure)?|units)$/i,
  structure: /^(bom\s*structure|make\s*\/\s*buy|make\s*or\s*buy|procurement|source|type)$/i,
};
export type Mapping = Partial<Record<Role, number>>;

/** The header row: the first of the top 15 rows that has a part number column. */
export function findHeader(t: Table) {
  for (let i = 0; i < Math.min(15, t.length); i++) if (t[i].some((c) => PATTERNS.partNumber.test(c.trim()))) return i;
  return 0;
}
export function guessMapping(header: string[]): Mapping {
  const m: Mapping = {};
  for (const r of ROLES) {
    // "Unit QTY" (Inventor) is per assembly; prefer it over a total "QTY" when both exist.
    const hits = header.map((h, i) => ({ h: h.trim(), i })).filter((x) => PATTERNS[r.id].test(x.h) && !Object.values(m).includes(x.i));
    if (!hits.length) continue;
    const pick = r.id === "quantity" ? hits.find((x) => /unit/i.test(x.h)) ?? hits[0] : r.id === "description" ? hits.find((x) => /desc/i.test(x.h)) ?? hits[0] : hits[0];
    m[r.id] = pick.i;
  }
  return m;
}

export type CadRow = { level: number; partNumber: string; description?: string; quantity: number; uom?: string; revision?: string; material?: string; replenishment?: "prod_order" | "purchase"; position?: string };

export function toRows(t: Table, headerAt: number, m: Mapping) {
  const out: CadRow[] = []; const skipped: number[] = [];
  const get = (r: string[], k: Role) => (m[k] === undefined ? "" : (r[m[k]!] ?? "").trim());
  t.slice(headerAt + 1).forEach((r, i) => {
    const pn = get(r, "partNumber");
    const qty = Number(get(r, "quantity").replace(/,/g, "").replace(/[^\d.\-]/g, "")) || (m.quantity === undefined ? 1 : 0);
    if (!pn) { skipped.push(headerAt + 2 + i); return; }
    const pos = get(r, "position");
    const lvlCol = get(r, "level");
    // Level: a number (1, 2…), or dots (".", "..", "..2") as some PDM exports write it; else from the item number.
    const level = /^\d+$/.test(lvlCol) ? Number(lvlCol) : /^\.+/.test(lvlCol) ? lvlCol.match(/^\.+/)![0].length : /^\d+(\.\d+)+$/.test(pos) ? pos.split(".").length : 1;
    const st = get(r, "structure").toLowerCase();
    out.push({
      level, partNumber: pn, quantity: qty, description: get(r, "description") || undefined, position: pos || undefined,
      revision: get(r, "revision") || undefined, material: get(r, "material") || undefined, uom: get(r, "uom") || undefined,
      replenishment: /purchas|buy|vendor/.test(st) ? "purchase" : undefined, // anything else: made if it has components
    });
  });
  // A Level column that starts at 0 means the top assembly is listed: drop it and shift.
  if (m.level !== undefined && out.length && out[0].level === 0) { out.shift(); }
  if (out.some((r) => r.level === 0)) for (const r of out) r.level += 1;
  return { rows: out, skipped };
}

/** "1000 Bicycle.SLDASM.xlsx" → part 1000, description Bicycle. */
export function topFromFileName(name: string) {
  const stem = name.replace(/\.(xlsx|xlsm|csv|txt|tsv)$/i, "").replace(/\.(sldasm|iam|asm)$/i, "").replace(/[_]+/g, " ").replace(/\s*(-\s*)?(bom|indented|parts only|export)\b.*$/i, "").trim();
  const m = stem.match(/^([A-Za-z0-9][\w.\-]*)\s+(.+)$/);
  return m ? { partNumber: m[1].slice(0, 20), description: m[2].slice(0, 100) } : { partNumber: stem.slice(0, 20), description: "" };
}
